import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PublicClientApplication, InteractionStatus, InteractionRequiredAuthError, EventType } from '@azure/msal-browser';
import { msalConfig, loginRequest, graphApiRequest } from '../authConfig';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(false);

  // Initialize MSAL and handle redirects
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        // Initialize MSAL
        await msalInstance.initialize();
        
        // Handle redirect response
        const response = await msalInstance.handleRedirectPromise();
        console.log('MSAL redirect response:', response);
        
        if (response) {
          // User just logged in via redirect
          console.log('Processing Azure login with ID token...');
          setIsProcessingRedirect(true);
          try {
            await handleAzureLogin(response);
            console.log('Azure login successful');
          } catch (loginErr) {
            console.error('Azure login failed:', loginErr);
            setError(loginErr.message || 'Azure login failed');
          } finally {
            setIsProcessingRedirect(false);
          }
        } else {
          // Check for existing session
          const storedUser = localStorage.getItem('user');
          const token = localStorage.getItem('token');
          
          if (storedUser && token) {
            setUser(JSON.parse(storedUser));
          }
        }
        
        setMsalInitialized(true);
      } catch (err) {
        console.error('MSAL initialization error:', err);
        
        // List of MSAL errors that should not be shown to users
        const ignorableErrors = [
          'redirect_in_iframe',
          'interaction_in_progress',
          'monitor_window_timeout',
          'failed_to_parse_response',
          'endpoints_resolution_error',
          'no_account_error'
        ];
        
        const errorCode = err.errorCode || err.message || '';
        const isIgnorableError = ignorableErrors.some(e => 
          errorCode.toLowerCase().includes(e.toLowerCase())
        );
        
        // Don't block the app if MSAL fails - allow local login
        setMsalInitialized(true);
        
        // Only show error for critical issues that aren't MSAL-specific
        if (!isIgnorableError) {
          setError('Microsoft login unavailable. Please use email/password login.');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeMsal();
  }, []);

  // Handle Azure AD login response
  const handleAzureLogin = async (response) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get ID token and access token from MSAL response
      const idToken = response.idToken;
      let accessToken = response.accessToken;
      
      if (!idToken) {
        throw new Error('No ID token in response');
      }

      // Try to get a Graph API access token if we don't have proper scopes
      // This is needed because CIAM tokens might not include User.Read scope
      if (accessToken) {
        try {
          const account = msalInstance.getAllAccounts()[0];
          if (account) {
            // Try to silently acquire token for Graph API
            const graphResponse = await msalInstance.acquireTokenSilent({
              ...graphApiRequest,
              account: account
            });
            if (graphResponse && graphResponse.accessToken) {
              console.log('Got separate Graph API token');
              accessToken = graphResponse.accessToken;
            }
          }
        } catch (graphErr) {
          console.log('Could not get Graph API token silently:', graphErr.message);
          // Continue with original access token
        }
      }

      // Exchange Azure token for our app token
      // Send both ID token and access token (access token can be used to call Graph API)
      const data = await authAPI.azureLogin(idToken, accessToken);
      
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      
      return data.user;
    } catch (err) {
      console.error('Azure login error:', err);
      const message = err.response?.data?.detail || err.message || 'Login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Login with Microsoft (redirect)
  const loginWithMicrosoft = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Start the redirect login flow
      await msalInstance.loginRedirect(loginRequest);
      
    } catch (err) {
      console.error('Microsoft login error:', err);
      const message = err.message || 'Microsoft login failed';
      setError(message);
      setLoading(false);
      throw new Error(message);
    }
  };

  // Demo login (for testing)
  const login = async (email) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authAPI.demoLogin(email);
      
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      
      return data.user;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Local login with password
  const loginWithPassword = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authAPI.localLogin(email, password);
      
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
      
      return data.user;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = useCallback(async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    
    // Also logout from MSAL if there's an active account
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        await msalInstance.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: window.location.origin,
        });
      } catch (err) {
        console.error('MSAL logout error:', err);
      }
    }
  }, []);

  // Acquire a Microsoft Graph access token with additional scopes via incremental consent.
  // Used by features (e.g. Planner task assignment) that need scopes beyond basic login.
  // Throws Error('NO_MSAL_ACCOUNT') if the user did not sign in via Microsoft SSO.
  const getGraphToken = async (scopes) => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('NO_MSAL_ACCOUNT');
    }
    const request = { scopes, account: accounts[0] };
    try {
      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await msalInstance.acquireTokenPopup(request);
        return response.accessToken;
      }
      throw err;
    }
  };

  const isSuperAdmin = () => user?.role === 'superadmin';
  const isAdmin = () => user?.role === 'admin' || user?.role === 'superadmin';
  const isPM = () => user?.role === 'pm';
  
  const canViewAllPartners = () => isAdmin();
  const getAssignedPartners = () => user?.assigned_partners || [];
  
  // Check if user is from Technofocus domain
  const isTechnofocusUser = () => {
    const domain = user?.domain?.toLowerCase();
    return domain === 'technofocus.in' || domain === 'technofocus.co';
  };

  const value = {
    user,
    loading,
    error,
    login,
    loginWithPassword,
    loginWithMicrosoft,
    logout,
    isSuperAdmin,
    isAdmin,
    isPM,
    canViewAllPartners,
    getAssignedPartners,
    isTechnofocusUser,
    isAuthenticated: !!user,
    msalInitialized,
    isProcessingRedirect,
    getGraphToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
